import { html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { cond } from '../common/directives/cond.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import componentStyle from '../common/styles/Component.css'
import style from './OutlineItem.css'

/**
 * Outline Item is a single link within an [Outline](/components/outline/). It
 * points at an in-page heading via its `href` and indents according to its
 * `level`. Its `active` state is managed by the parent Outline — you normally
 * don't set it yourself.
 *
 * @status draft
 * @category navigation
 *
 * @slot - The label text for the heading this item links to.
 *
 * @cssprop [--n-outline-item-color=var(--n-color-text-weak)] - Text color in the default state.
 * @cssprop [--n-outline-item-color-hover=var(--n-color-text)] - Text color on hover.
 * @cssprop [--n-outline-item-color-active=var(--n-color-text-link)] - Text color when the item is active.
 *
 * @parent Outline
 */
@customElement('nord-outline-item')
export default class OutlineItem extends DraftComponentMixin(LitElement) {
  static styles = [componentStyle, style]

  /**
   * The URL fragment of the heading this item links to, e.g. `#installation`.
   */
  @property({ reflect: true }) href = ''

  /**
   * Heading depth from 1 to 4. Controls how far the item is indented.
   */
  @property({ type: Number, reflect: true }) level: 1 | 2 | 3 | 4 = 1

  /**
   * Whether this item represents the currently active section. Managed by the
   * parent [Outline](/components/outline/) via its scroll-spy or `active` property.
   */
  @property({ type: Boolean, reflect: true }) active = false

  render() {
    return html`
      <a class="n-outline-item" href=${this.href} aria-current=${cond(this.active, 'location')}>
        <slot></slot>
      </a>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-outline-item': OutlineItem
  }
}
